import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { SCHEMA_RITORNO, completaAccessoNativo } from "@/integrations/supabase/accessoNativo";
import { chiudiBrowserInApp } from "@/lib/apriEsterno";
import { EVENTO_RITORNO } from "@/lib/accessoWeb";

/**
 * Il ritorno a casa dopo l'accesso con Google o Apple, DENTRO l'app nativa.
 *
 * IL PROBLEMA (Davide dal telefono, 29/08): dall'app il login apriva Chrome,
 * il giro con Google riusciva... e la persona restava in Chrome, dentro la
 * versione web. L'app non vedeva mai la sessione. Il motivo: l'app
 * impacchettata vive su un indirizzo interno (capacitor://localhost) che
 * Supabase non accetta come destinazione, quindi ripiegava sul sito.
 *
 * LA SOLUZIONE e' in due meta':
 * 1. AccessiSocial, quando gira dentro l'app, chiede a Supabase di tornare a
 *    `it.datacomtecnologie.ai://auth/callback` - uno schema che il telefono
 *    riconosce come "questo lo apre l'app" (registrato in AndroidManifest e
 *    Info.plist, e nella lista dei redirect di Supabase).
 * 2. Questo componente ascolta l'apertura: quando l'app viene riaperta con
 *    quell'indirizzo, il CODICE nell'indirizzo viene scambiato con la sessione
 *    (flusso PKCE, vedi accessoNativo.ts) e si prosegue su /auth/callback come
 *    sul web. Da li' in poi il giro e' identico: profilo esistente → chat;
 *    profilo senza partita IVA → completa-profilo.
 *
 * Fino al 05/09/2026 qui si accettavano i TOKEN scritti nel frammento
 * dell'indirizzo, di chiunque fossero (audit, Sol 2): un link confezionato
 * apriva l'app dentro l'account di un altro. Ora il frammento e' ignorato.
 *
 * Sul web questo componente non fa niente: non c'e' nessun "ritorno" da
 * ascoltare, il browser resta dov'e'.
 */
export function RitornoAccessoNativo() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const chiudiIlGiro = async (url: string) => {
      if (!url.startsWith(SCHEMA_RITORNO)) return;
      // Il browser di sistema resta aperto SOPRA l'app: se non lo chiudiamo,
      // chi ha appena fatto l'accesso vede una pagina bianca e crede che sia
      // bloccato. Prima di tutto il resto, e senza aspettare che finisca.
      // (Con la finestra di ASWebAuthenticationSession non c'e' niente da
      // chiudere: si chiude da sola, e la chiamata non fa danno.)
      void chiudiBrowserInApp();
      const esito = await completaAccessoNativo(url);
      // Niente codice o scambio rifiutato: si atterra sul login con i piedi
      // per terra invece di restare su una schermata qualunque.
      navigate(esito === "ok" ? "/auth/callback" : "/login", { replace: true });
    };

    // Android e i gusci vecchi: il telefono riapre l'app col deep link.
    const promessa = CapApp.addListener("appUrlOpen", async ({ url }) => {
      // `async` e atteso: chi lancia l'apertura nelle prove aspetta questa
      // promessa per sapere quando il giro e' finito. Restituendo `void` il
      // test passava prima che la sessione fosse consegnata.
      await chiudiIlGiro(url);
    });

    // iOS dalla build 4: la finestra di Apple consegna l'indirizzo qui dentro,
    // senza passare dal deep link. Vedi lib/accessoWeb.ts.
    const daFinestra = (e: Event) => {
      const url = (e as CustomEvent<string>).detail;
      if (typeof url === "string") void chiudiIlGiro(url);
    };
    window.addEventListener(EVENTO_RITORNO, daFinestra);

    return () => {
      promessa.then((l) => l.remove());
      window.removeEventListener(EVENTO_RITORNO, daFinestra);
    };
  }, [navigate]);

  return null;
}
